import { ModuleNameEnum } from "@utilities/enums";
import { useAllowedAccess, useOrganisation, useSubscriptions } from "@utilities/hooks";
import { SelectedModule } from "@utilities/types";
import { useCallback, useEffect, useMemo, useState } from "react";

export const useControllers = () => {

    useAllowedAccess();
    const {
        organisationMontlyRevenue,
        getOrganisationMontlyRevenueError,
        onGetOrganisationMontlyRevenue
    } = useOrganisation();

    const {
        InvoiceHistory,
        plans, modules, organisationModules,
        onGetAllPlans, onInitSubscriptionModule,
        onGetOrganisationSubscriptionModules,
        onGetOrganisationInvoiceHistory,
    } = useSubscriptions();

    const [moduleInfo, setModuleInfo] = useState<string>("");
    const [selectedOptions, setSelectedOptions] = useState<SelectedModule[]>([]);

    const disableModulesUpdatingButton = useMemo(()=> (
        JSON.stringify(organisationModules?.modules) === JSON.stringify(selectedOptions)
    ), [organisationModules, selectedOptions]);

    const currentPlan = useMemo(() => {
        if(plans.length === 0 || !organisationMontlyRevenue) return null;
        return plans.find(plan => (organisationMontlyRevenue?.totalRevenue || 0) <= plan.seuil) || plans[plans.length - 1];
    }, [organisationMontlyRevenue, plans]);

    const total = useMemo(() => {
        return Object.values(selectedOptions).reduce((sum, opt) => sum + opt.price, 0);
    }, [selectedOptions]);

    useEffect(()=> {
        onGetOrganisationMontlyRevenue();
        onGetAllPlans();
        onGetOrganisationSubscriptionModules();
        onGetOrganisationInvoiceHistory();
    }, []);

    useEffect(()=> {
        if(!organisationModules){
            onInitSubscriptionModule();
        } else {
            setSelectedOptions(organisationModules.modules)
        }
    }, [organisationModules]);

    const handleSelect = useCallback(
        (moduleName: string, option: { label: string; price: number }) => {

            const newModule: SelectedModule = {
                name: moduleName,
                option: option.label,
                price: option.price,
                activatedAt: new Date()
            };

            // Si on sélectionne Pack illimité
            if (moduleName === ModuleNameEnum.unlimitedPack) {
                setSelectedOptions([newModule]);
                return;
            }

            // Si un autre module est sélectionné et Pack illimité est actif → reset
            if (selectedOptions.find(module => module.name === ModuleNameEnum.unlimitedPack)) {
                // On exclut le pack illimité et on garde les modules avec l'option sélectionnée ou celle par défaut
                const newSelection: SelectedModule[] = modules
                    .filter(module => module.name !== ModuleNameEnum.unlimitedPack)
                    .map(module => {
                    if (module.name === moduleName) {
                        return newModule;
                    }

                    const defaultOption = module.options[0];

                    return {
                        name: module.name,
                        option: defaultOption.label,
                        price: defaultOption.price,
                        activatedAt: new Date(),
                    };
                    });

                setSelectedOptions(newSelection);
                return;
            }

            const existingModuleIndex = selectedOptions.findIndex(module => module.name === moduleName);
            if (existingModuleIndex !== -1) {
                // Si le module existe déjà, on met à jour son option
                const updatedOptions = [...selectedOptions];
                updatedOptions[existingModuleIndex] = newModule;
                setSelectedOptions(updatedOptions);
            } else {
                // Si le module n'existe pas, on l'ajoute
                setSelectedOptions([...selectedOptions, newModule]);
            }
    }, [modules, selectedOptions]);

    return {
        plans, selectedOptions,
        modules, InvoiceHistory,
        currentPlan, disableModulesUpdatingButton,
        organisationMontlyRevenue,
        total, moduleInfo, setModuleInfo,
        handleSelect,
    };
};
